let observer = null
let domMutationObserver = null
const observedElements = new Set()
const elementTimers = new Map() // 存储每个元素的防抖定时器
const elementStates = new Map() // 存储每个元素的状态（是否已触发过动画）
const VISIBLE_CLASS = 'is-visible'
const DEBOUNCE_DELAY = 150 // 防抖延迟时间（毫秒）
const DEFAULT_OPTIONS = {
  // threshold: 0.2,
  // rootMargin: '0px 0px -10% 0px'
  threshold: 0,
  rootMargin: '0px 0px -5% 0px'
}

const prefersReducedMotion = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

const shouldObserveElement = (el) => {
  if (!(el instanceof HTMLElement)) {
    return false
  }

  // 只处理包含 animate- 类名或显式标记的数据属性的元素
  if (el.dataset.animateOnScroll === 'true') {
    return true
  }

  return Array.from(el.classList).some((className) => className.startsWith('animate-'))
}

const resetAnimationState = (el) => {
  // 清除防抖定时器
  if (elementTimers.has(el)) {
    clearTimeout(elementTimers.get(el))
    elementTimers.delete(el)
  }
  
  el.classList.remove(VISIBLE_CLASS)
  elementStates.set(el, false)
  // 访问 offsetHeight 触发一次 reflow，确保后续动画可以重新开始
  void el.offsetHeight
}

const applyVisibleState = (el) => {
  // 如果元素已经处于可见状态则无需处理
  if (el.classList.contains(VISIBLE_CLASS)) {
    return
  }

  // 清除之前的防抖定时器
  if (elementTimers.has(el)) {
    clearTimeout(elementTimers.get(el))
  }

  // 使用防抖延迟执行动画
  const timer = setTimeout(() => {
    // 再次检查元素是否仍然需要动画（可能在延迟期间状态已改变）
    if (!observedElements.has(el) || !el.isConnected) {
      elementTimers.delete(el)
      return
    }

    // 如果已经处于可见状态，直接返回
    if (el.classList.contains(VISIBLE_CLASS)) {
      elementStates.set(el, true)
      elementTimers.delete(el)
      return
    }

    // 在重新添加可见状态前确保动画状态已被重置
    el.classList.remove(VISIBLE_CLASS)
    void el.offsetHeight // 触发 reflow
    el.classList.add(VISIBLE_CLASS)
    elementStates.set(el, true)
    elementTimers.delete(el)
  }, DEBOUNCE_DELAY)

  elementTimers.set(el, timer)
}

const createObserver = () => {
  if (observer) {
    return observer
  }

  observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const el = entry.target

      if (!observedElements.has(el)) {
        return
      }

      if (!el.isConnected) {
        observer.unobserve(el)
        observedElements.delete(el)
        // 清理相关状态
        if (elementTimers.has(el)) {
          clearTimeout(elementTimers.get(el))
          elementTimers.delete(el)
        }
        elementStates.delete(el)
        return
      }

      const hasTriggered = elementStates.get(el) === true
      const intersectionRatio = entry.intersectionRatio

      if (entry.isIntersecting) {
        // 如果元素已经触发过动画，且仍然在可视区域内，不再重复触发
        if (hasTriggered && el.classList.contains(VISIBLE_CLASS)) {
          return
        }
        applyVisibleState(el)
      } else {
        // 只有当元素完全离开可视区域（intersectionRatio 为 0）时才重置
        // 这样可以避免在边缘时频繁切换
        if (intersectionRatio === 0) {
          resetAnimationState(el)
        }
      }
    })
  }, DEFAULT_OPTIONS)

  return observer
}

const registerElement = (el) => {
  if (observedElements.has(el)) {
    return
  }

  const io = createObserver()
  observedElements.add(el)
  io.observe(el)
}

export const initScrollAnimations = () => {
  if (typeof window === 'undefined') {
    return
  }

  const candidates = document.querySelectorAll('[data-animate-on-scroll], [class*="animate-"]')

  if (prefersReducedMotion()) {
    candidates.forEach((el) => {
      if (shouldObserveElement(el)) {
        el.classList.add(VISIBLE_CLASS)
      }
    })
    return
  }

  candidates.forEach((el) => {
    if (shouldObserveElement(el)) {
      registerElement(el)
    }
  })

  // 监听后续动态渲染内容（如轮播、异步组件）
  if (!domMutationObserver && typeof MutationObserver !== 'undefined') {
    domMutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof HTMLElement)) {
            return
          }
          if (shouldObserveElement(node)) {
            registerElement(node)
          }
          const descendants = node.querySelectorAll?.('[data-animate-on-scroll], [class*="animate-"]')
          if (descendants && descendants.length) {
            descendants.forEach((el) => {
              if (shouldObserveElement(el)) {
                registerElement(el)
              }
            })
          }
        })
      })
    })

    domMutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    })
  }
}

export const destroyScrollAnimations = () => {
  // 清理所有防抖定时器
  elementTimers.forEach((timer) => {
    clearTimeout(timer)
  })
  elementTimers.clear()
  elementStates.clear()

  if (observer) {
    observedElements.forEach((el) => {
      observer.unobserve(el)
      el.classList.remove(VISIBLE_CLASS)
    })

    observedElements.clear()
    observer.disconnect()
    observer = null
  }

  if (domMutationObserver) {
    domMutationObserver.disconnect()
    domMutationObserver = null
  }
}

